'use client';

import * as React from 'react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Autoscroll from 'embla-carousel-auto-scroll';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './button';
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
type EmblaCarouselType = ReturnType<typeof useEmblaCarousel>[1];
type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  onSelect?: () => void;
};
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: EmblaCarouselType;
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;
const CarouselContext = React.createContext<CarouselContextProps | null>(null);
function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }
  return context;
}
const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  onSelect,
  ...props
}, ref) => {
  const [carouselRef, api] = useEmblaCarousel({
    ...opts,
    axis: orientation === 'horizontal' ? 'x' : 'y'
  }, plugins);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const handleSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    onSelect?.();
  }, [onSelect, api]);
  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollPrev, scrollNext]);
  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    handleSelect(api);
    api.on('reInit', handleSelect);
    api.on('select', handleSelect);
    return () => {
      api?.off('select', handleSelect);
    };
  }, [api, handleSelect]);
  return <CarouselContext.Provider value={{
    carouselRef,
    api: api,
    opts,
    orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext
  }}>
        <div ref={ref} onKeyDownCapture={handleKeyDown} className={cn('relative', className)} role="region" aria-roledescription="carousel" {...props}>
          {children}
        </div>
      </CarouselContext.Provider>;
});
Carousel.displayName = 'Carousel';
const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  containerClassName?: string;
}>(({
  className,
  containerClassName,
  ...props
}, ref) => {
  const {
    carouselRef,
    orientation
  } = useCarousel();
  return <div ref={carouselRef} className={cn('overflow-hidden', containerClassName)}>
      <div ref={ref} className={cn('flex h-full', orientation === 'horizontal' ? '' : 'flex-col', className)} {...props} />
    </div>;
});
CarouselContent.displayName = 'CarouselContent';
const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({
  className,
  ...props
}, ref) => {
  const {
    orientation
  } = useCarousel();
  return <div ref={ref} role="group" aria-roledescription="slide" className={cn('min-w-0 shrink-0 grow-0 basis-full', className)} {...props} />;
});
CarouselItem.displayName = 'CarouselItem';
const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}, ref) => {
  const {
    orientation,
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  return <Button ref={ref} variant={variant} size={size} className={cn('absolute h-8 w-8 rounded-full', className)} disabled={!canScrollPrev} onClick={scrollPrev} {...props}>
        <ArrowLeft className="h-full w-full stroke-1" />
        <span className="sr-only">Previous slide</span>
      </Button>;
});
CarouselPrevious.displayName = 'CarouselPrevious';
const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}, ref) => {
  const {
    orientation,
    scrollNext,
    canScrollNext
  } = useCarousel();
  return <Button ref={ref} variant={variant} size={size} className={cn('absolute h-8 w-8 rounded-full', className)} disabled={!canScrollNext} onClick={scrollNext} {...props}>
        <ArrowRight className="h-full w-full stroke-1" />
        <span className="sr-only">Next slide</span>
      </Button>;
});
CarouselNext.displayName = 'CarouselNext';
const DetachedCarouselDots = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  api: CarouselApi;
  variant?: 'primary' | 'secondary';
}>(({
  className,
  api,
  variant = 'primary',
  ...props
}, ref) => {
  const {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  } = useDotButton(api);
  return <div className="flex items-center space-x-2 justify-center p-4" {...props}>
      {scrollSnaps.map((_, index) => <button type="button" key={index} onClick={() => onDotButtonClick(index)} className={cn('w-2 aspect-square rounded-full bg-gray-400 transition-colors duration-300 outline-4 outline-transparent', {
      'bg-clippd-pink': variant === 'primary' && selectedIndex === index,
      'bg-pl-blue-600': variant === 'secondary' && selectedIndex === index
    })} />)}
    </div>;
});
DetachedCarouselDots.displayName = 'DetachedCarouselDots';
const CarouselDots = React.forwardRef<HTMLDivElement, {
  variant?: 'primary' | 'secondary';
}>((props, ref) => {
  const {
    api
  } = useCarousel();
  return <DetachedCarouselDots ref={ref} api={api} {...props} />;
});
CarouselDots.displayName = 'CarouselDots';
type UseDotButtonType = {
  selectedIndex: number;
  scrollSnaps: number[];
  onDotButtonClick: (index: number) => void;
};
export const useDotButton = (emblaApi: EmblaCarouselType): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const onDotButtonClick = React.useCallback((index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  }, [emblaApi]);
  const onInit = React.useCallback((emblaApi: EmblaCarouselType) => {
    if (emblaApi) {
      setScrollSnaps(emblaApi.scrollSnapList());
    }
  }, [emblaApi]);
  const onSelect = React.useCallback((emblaApi: EmblaCarouselType) => {
    if (emblaApi) {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);
  React.useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);
  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  };
};
type UseAutoplayType = {
  autoplayIsPlaying: boolean;
  toggleAutoplay: () => void;
};
const useAutoplay = (emblaApi: EmblaCarouselType | undefined): UseAutoplayType => {
  const [autoplayIsPlaying, setAutoplayIsPlaying] = React.useState(false);
  const toggleAutoplay = React.useCallback(() => {
    const autoplay: any = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play;
    playOrStop();
  }, [emblaApi]);
  React.useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    setAutoplayIsPlaying(autoplay.isPlaying());
    emblaApi.on('autoplay:play', () => setAutoplayIsPlaying(true)).on('autoplay:stop', () => setAutoplayIsPlaying(false)).on('reInit', () => setAutoplayIsPlaying(autoplay.isPlaying()));
  }, [emblaApi]);
  return {
    autoplayIsPlaying,
    toggleAutoplay
  };
};
export { type CarouselApi, useCarousel, useAutoplay, useEmblaCarousel, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselDots, DetachedCarouselDots, Autoplay, Autoscroll };